/**
 * « Mes inscriptions » : quelles semaines, pour quel enfant, et dans quel état.
 *
 * ## Groupé par enfant, pas par date d'achat
 *
 * L'ancienne section listait les trois dernières inscriptions dans l'ordre où
 * elles avaient été créées. Une famille de trois enfants y voyait trois lignes
 * qui pouvaient concerner le même enfant, sans jamais savoir si le deuxième
 * avait sa place. La question d'un parent est « où en est chacun de mes
 * enfants », et le regroupement y répond directement.
 *
 * ## Les semaines à venir d'abord, les passées repliées
 *
 * Ce qui reste à vivre tient en haut ; l'historique existe mais ne pousse pas
 * le reste hors de l'écran. Sur un téléphone, ce qu'on ne voit pas sans faire
 * défiler trois fois n'existe pas.
 *
 * ## Le montant n'y est pas
 *
 * Il n'était pas juste — `registrations.payment_status` ne suit plus le
 * rapprochement bancaire — et surtout ce n'est pas ce qu'on vient chercher ici.
 * Les factures ont leur page.
 */

import { Link } from 'react-router-dom';
import { useState } from 'react';
import { CalendarDays, ChevronDown, MapPin } from 'lucide-react';
import {
  estPassee,
  periodeLisible,
  statutInscription,
  type StatutAnnulation,
} from '../../lib/inscriptions/statut';
import { PastilleInscription } from './Pastille';

export interface InscriptionAffichee {
  id: string;
  kid_id: string;
  cancellation_status: StatutAnnulation | string | null;
  session: {
    stage: { title: string };
    start_date: string;
    end_date: string;
    center: { name: string };
  };
}

const Semaine = ({ inscription }: { inscription: InscriptionAffichee }) => {
  const statut = statutInscription(inscription.cancellation_status);
  const { session } = inscription;

  return (
    <li className="rounded-xl bg-white p-3 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 font-medium text-gray-900">{session.stage.title}</p>
        <PastilleInscription statut={statut} />
      </div>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
        <CalendarDays className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        {periodeLisible(session.start_date, session.end_date)}
      </p>
      <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-600">
        <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        {session.center.name}
      </p>
      {statut.detail && <p className="mt-1.5 text-sm text-amber-800">{statut.detail}</p>}
    </li>
  );
};

const GroupeEnfant = ({
  prenom,
  aVenir,
  passees,
}: {
  prenom: string;
  aVenir: InscriptionAffichee[];
  passees: InscriptionAffichee[];
}) => {
  const [historiqueOuvert, setHistoriqueOuvert] = useState(false);

  return (
    <div>
      {/*
        La taille est posee explicitement : `index.css` donne aux `h3` un
        `text-xl md:text-2xl` global, qui rendait le prenom d'un enfant plus
        gros que le titre « Mes inscriptions » au-dessus de lui.
      */}
      <h3 className="mb-2 text-base font-semibold text-gray-900">{prenom}</h3>

      {aVenir.length === 0 ? (
        <p className="mb-2 text-sm text-gray-500">Aucune semaine à venir.</p>
      ) : (
        <ul className="mb-2 space-y-2">
          {aVenir.map((i) => (
            <Semaine key={i.id} inscription={i} />
          ))}
        </ul>
      )}

      {passees.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setHistoriqueOuvert((v) => !v)}
            aria-expanded={historiqueOuvert}
            className="flex min-h-[44px] items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${historiqueOuvert ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
            {passees.length === 1
              ? '1 semaine déjà passée'
              : `${passees.length} semaines déjà passées`}
          </button>
          {historiqueOuvert && (
            <ul className="mt-2 space-y-2">
              {passees.map((i) => (
                <Semaine key={i.id} inscription={i} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

const Inscriptions = ({
  inscriptions,
  enfants,
}: {
  inscriptions: InscriptionAffichee[];
  /** Pour nommer les groupes dans l'ordre de la liste des enfants. */
  enfants: { id: string; prenom: string }[];
}) => {
  if (inscriptions.length === 0) {
    return (
      <p className="text-gray-600">
        Aucune inscription pour le moment.{' '}
        <Link to="/activities" className="font-medium text-primary-700 hover:text-primary-800">
          Découvrir les stages
        </Link>
      </p>
    );
  }

  /*
    L'ordre suit celui des enfants, et non celui des inscriptions : la liste
    ne change donc pas de forme d'une visite à l'autre. Un enfant dont toutes
    les inscriptions ont disparu de la vue (session masquée, compte modifié)
    ferait autrement apparaître un groupe sans nom.
  */
  const groupes = enfants
    .map((e) => {
      const siennes = inscriptions.filter((i) => i.kid_id === e.id);
      return {
        id: e.id,
        prenom: e.prenom,
        aVenir: siennes.filter((i) => !estPassee(i.session.end_date)),
        passees: siennes.filter((i) => estPassee(i.session.end_date)),
      };
    })
    .filter((g) => g.aVenir.length > 0 || g.passees.length > 0);

  /* Une inscription dont l'enfant n'est plus dans la liste reste visible. */
  const orphelines = inscriptions.filter((i) => !enfants.some((e) => e.id === i.kid_id));

  return (
    <div className="space-y-5">
      {groupes.map((g) => (
        <GroupeEnfant key={g.id} prenom={g.prenom} aVenir={g.aVenir} passees={g.passees} />
      ))}

      {orphelines.length > 0 && (
        <GroupeEnfant
          prenom="Autres inscriptions"
          aVenir={orphelines.filter((i) => !estPassee(i.session.end_date))}
          passees={orphelines.filter((i) => estPassee(i.session.end_date))}
        />
      )}

      <Link
        to="/registrations"
        className="inline-flex min-h-[44px] items-center font-medium text-primary-700 hover:text-primary-800"
      >
        Toutes mes inscriptions, et demander une annulation
      </Link>
    </div>
  );
};

export default Inscriptions;
